
import api from './axios';

/**
 * Helper function to extract error message from API response
 */
export const getConversationErrorMessage = (error) => {
  if (error && error.response && error.response.data) {
    if (error.response.data.message) {
      return error.response.data.message;
    }
    if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
      return error.response.data.errors[0];
    }
    if (error.response.data.errors && typeof error.response.data.errors === "object") {
      const firstError = Object.values(error.response.data.errors)[0];
      if (Array.isArray(firstError)) {
        return firstError[0];
      }
      return String(firstError);
    }
  }
  return error?.message || "An unexpected error occurred";
};

/**
 * Conversations API service
 * Handles all conversation-related operations
 */
export const conversationsApi = {
  // Get a conversation by ID (optionally with messages)
  getConversation: async (id, includeMessages = false) => {
    try {
      const response = await api.get(`/conversations/${id}?includeMessages=${includeMessages}`);
      return response.data;
    } catch (error) {
      return { error: getConversationErrorMessage(error) };
    }
  },


  getUnreadCount: async (conversationId) => {
  try {
    const response = await api.get(`/messages/conversation/${conversationId}/unread-count`);
    return response.data;
  } catch (error) {
    return { error: "Failed to load unread count" };
  }
},




  // Get client conversations (filter by status or operator)
  getClientConversations: async (clientId, status = null, operatorId = null) => {
    try {
      let url = `/conversations/client/${clientId}`;
      const params = [];
      if (status) params.push(`status=${encodeURIComponent(status)}`);
      if (operatorId) params.push(`operatorId=${operatorId}`);
      if (params.length) url += `?${params.join('&')}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      return { error: getConversationErrorMessage(error) };
    }
  },

  // Create a conversation
  createConversation: async (data) => {
    try {
      const response = await api.post('/conversations', data);
      return response.data;
    } catch (error) {
      return { error: getConversationErrorMessage(error) };
    }
  },

  // Get or create a conversation
  getOrCreateConversation: async (data) => {
    try {
      const response = await api.post('/conversations/get-or-create', data);
      return response.data;
    } catch (error) {
      return { error: getConversationErrorMessage(error) };
    }
  },

  // Lock a conversation
  lockConversation: async (conversationId, data) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/lock`, data);
      return response.data;
    } catch (error) {
      return { error: getConversationErrorMessage(error) };
    }
  },

  // Unlock a conversation
  unlockConversation: async (conversationId) => {
    try {
      const response = await api.post(`/conversations/${conversationId}/unlock`);
      return response.data;
    } catch (error) {
      return { error: getConversationErrorMessage(error) };
    }
  },

  // Get operator conversations
  getOperatorConversations: async (operatorId) => {
    try {
      const response = await api.get(`/conversations/operator/${operatorId}`);
      return response.data;
    } catch (error) {
      return { error: getConversationErrorMessage(error) };
    }
  },

  // Get lock settings (for timer configuration)
  getLockSettings: async () => {
    try {
      const response = await api.get('/conversations/lock-settings');
      return response.data;
    } catch (error) {
      // Return default if API fails
      return { data: { lockDurationMinutes: 5, lockDurationMs: 5 * 60 * 1000 } };
    }
  },
};
